/*
 * RunWorkflowDialog — modal dialog for selecting capture documents
 * to execute the workflow against. Supports multi-select with checkboxes,
 * shift-click range selection, and select-all.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { captureAPI } from '../../services/api';
import './RunWorkflowDialog.css';

function formatDate(isoString) {
  if (!isoString) return '';
  try {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function getDocIcon(mimeType) {
  if (mimeType?.includes('pdf')) return '\uD83D\uDCC4';
  if (mimeType?.includes('image')) return '\uD83D\uDDBC\uFE0F';
  return '\uD83D\uDCC1';
}

function statusBadgeClass(status) {
  if (!status) return '';
  if (status === 'published' || status === 'approved') return 'ocr-completed';
  if (status === 'ocr_complete') return 'extracted';
  if (status === 'pending_review' || status === 'needs_review') return 'pending_review';
  if (status === 'ocr_failed' || status === 'processing_failed' || status === 'rejected') return 'ocr-failed';
  if (status === 'processing_ocr' || status === 'processing_extraction') return 'ocr-processing';
  return 'ocr-pending';
}

function friendlyStatus(status) {
  if (!status) return '';
  const map = {
    pending_ocr: 'Pending OCR',
    processing_ocr: 'OCR Running',
    ocr_complete: 'OCR Done',
    ocr_failed: 'OCR Failed',
    processing_extraction: 'Extracting',
    pending_review: 'Needs Review',
    processing_failed: 'Failed',
    approved: 'Approved',
    rejected: 'Rejected',
    published: 'Published',
  };
  return map[status] || status;
}

const RunWorkflowDialog = ({ open, onClose, onExecute }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  const [showCompleted, setShowCompleted] = useState(false);
  const lastClickedIndexRef = useRef(null);
  const searchRef = useRef(null);

  // Fetch documents on open
  useEffect(() => {
    if (!open) return;
    setSelectedDocIds(new Set());
    lastClickedIndexRef.current = null;
    setSearch('');
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const res = await captureAPI.listDocuments({ limit: 100 });
        if (!cancelled) {
          setDocuments(res.documents || []);
        }
      } catch (err) {
        console.error('[RunWorkflowDialog] Failed to load documents:', err);
        if (!cancelled) setDocuments([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open]);

  // Auto-focus search on open
  useEffect(() => {
    if (open && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  // Filter & sort (memoised-ish via render)
  const searchLower = search.toLowerCase().trim();
  const hideCompleted = !showCompleted;
  const filtered = documents
    .filter((doc) => {
      // Never runnable, whatever the toggle: a batch held for boundary review
      // (slice 3c) has a paused split; running a workflow on it would process
      // the whole scan as one document.
      if (doc.status === 'pending_boundary_review') return false;
      // Hide completed/archived documents unless toggled on
      if (hideCompleted && ['published', 'approved', 'rejected'].includes(doc.status)) return false;
      if (!searchLower) return true;
      const name = (doc.filename || doc.original_filename || '').toLowerCase();
      const type = (doc.document_type || '').toLowerCase();
      return name.includes(searchLower) || type.includes(searchLower);
    })
    .sort((a, b) => {
      // Active documents first, then completed at the bottom
      const completedStatuses = ['published', 'approved', 'rejected'];
      const aCompleted = completedStatuses.includes(a.status) ? 1 : 0;
      const bCompleted = completedStatuses.includes(b.status) ? 1 : 0;
      if (aCompleted !== bCompleted) return aCompleted - bCompleted;
      const aPriority = ['extracted', 'ocr_complete', 'pending_review'].includes(a.status) ? 0 : 1;
      const bPriority = ['extracted', 'ocr_complete', 'pending_review'].includes(b.status) ? 0 : 1;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });

  // ── Row click handler with shift-click range selection ──
  const handleRowClick = useCallback((docId, index, event) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);

      if (event.shiftKey && lastClickedIndexRef.current !== null) {
        // Range select between last clicked and current
        const start = Math.min(lastClickedIndexRef.current, index);
        const end = Math.max(lastClickedIndexRef.current, index);
        for (let i = start; i <= end; i++) {
          if (filtered[i]) next.add(filtered[i].id);
        }
      } else {
        // Toggle single item
        if (next.has(docId)) {
          next.delete(docId);
        } else {
          next.add(docId);
        }
      }

      return next;
    });
    lastClickedIndexRef.current = index;
  }, [filtered]);

  // ── Double-click runs just that single document ──
  const handleRowDoubleClick = useCallback((docId) => {
    onExecute([docId]);
  }, [onExecute]);

  // ── Select All / Deselect All ──
  const allVisibleSelected = filtered.length > 0 && filtered.every((d) => selectedDocIds.has(d.id));
  const someVisibleSelected = filtered.some((d) => selectedDocIds.has(d.id));

  const handleSelectAll = useCallback(() => {
    setSelectedDocIds((prev) => {
      if (allVisibleSelected) {
        // Deselect all visible
        const next = new Set(prev);
        for (const doc of filtered) next.delete(doc.id);
        return next;
      } else {
        // Select all visible
        const next = new Set(prev);
        for (const doc of filtered) next.add(doc.id);
        return next;
      }
    });
  }, [filtered, allVisibleSelected]);

  // ── Run button ──
  const selCount = selectedDocIds.size;

  const handleRun = useCallback(() => {
    if (selCount === 0) return;
    onExecute(Array.from(selectedDocIds));
  }, [selectedDocIds, selCount, onExecute]);

  if (!open) return null;

  return (
    <div className="rwd-overlay" onClick={handleOverlayClick}>
      <div className="rwd-dialog">
        {/* Header */}
        <div className="rwd-header">
          <h3 className="rwd-title">Run Workflow</h3>
          <button className="rwd-close-btn" onClick={onClose} title="Close">&times;</button>
        </div>

        {/* Search + Select All */}
        <div className="rwd-search-bar">
          <input
            ref={searchRef}
            className="rwd-search-input"
            type="text"
            placeholder="Search documents by filename..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {!loading && (
          <div className="rwd-select-all-bar">
            {filtered.length > 0 && (
              <label className="rwd-select-all-label" onClick={handleSelectAll}>
                <span className={`rwd-checkbox ${allVisibleSelected ? 'checked' : someVisibleSelected ? 'indeterminate' : ''}`} />
                <span>{allVisibleSelected ? 'Deselect All' : 'Select All'}</span>
                <span className="rwd-select-all-count">({filtered.length})</span>
              </label>
            )}
            <label className="rwd-show-completed-label" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#666', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Show completed
            </label>
          </div>
        )}

        {/* Document list */}
        <div className="rwd-body">
          {loading && (
            <div className="rwd-loading">Loading documents...</div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="rwd-empty">
              {search ? 'No documents match your search' : 'No documents found'}
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <ul className="rwd-doc-list">
              {filtered.map((doc, idx) => {
                const isSelected = selectedDocIds.has(doc.id);
                return (
                  <li
                    key={doc.id}
                    className={`rwd-doc-row ${isSelected ? 'selected' : ''}`}
                    onClick={(e) => handleRowClick(doc.id, idx, e)}
                    onDoubleClick={() => handleRowDoubleClick(doc.id)}
                  >
                    <span className={`rwd-checkbox ${isSelected ? 'checked' : ''}`} />
                    <div className="rwd-doc-icon">{getDocIcon(doc.mime_type)}</div>
                    <div className="rwd-doc-info">
                      <div className="rwd-doc-filename">
                        {doc.filename || doc.original_filename || doc.id}
                      </div>
                      <div className="rwd-doc-meta">
                        <span className="rwd-doc-date">{formatDate(doc.created_at)}</span>
                        {doc.status && (
                          <span className={`rwd-doc-badge ${statusBadgeClass(doc.status)}`}>
                            {friendlyStatus(doc.status)}
                          </span>
                        )}
                        {doc.document_type && (
                          <span className="rwd-doc-date">{doc.document_type}</span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="rwd-footer">
          <span className="rwd-footer-info">
            {!loading && (
              selCount > 0
                ? `${selCount} document${selCount !== 1 ? 's' : ''} selected`
                : `${filtered.length} document${filtered.length !== 1 ? 's' : ''}`
            )}
          </span>
          <div className="rwd-footer-actions">
            <button className="rwd-btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              className="rwd-btn-run"
              onClick={handleRun}
              disabled={selCount === 0}
            >
              ▶ Run{selCount > 0 ? ` (${selCount})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunWorkflowDialog;
